// pages/customers/CustomersTable.tsx
import { Edit, Trash2, ArrowRightLeft, Eye, Headset, ShoppingCart } from 'lucide-react'
import type { Customer } from '@/types/customer'
import { VIP_LEVEL_OPTIONS } from '@/types/customer-category'
import { PermissionGuard } from '@/components/auth/PermissionGuard'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'

interface CustomersTableProps {
  data?: Customer[]
  isLoading: boolean
  onEdit: (customer: Customer) => void
  onViewDetail: (customer: Customer) => void
  onDelete: (customer: Customer) => void
  onTransfer: (customer: Customer) => void
  onManualOrder: (customer: Customer) => void
  onRecordService: (customer: Customer) => void
  preparingCustomerId?: number | null
  isPreparingManualOrder?: boolean
}

const genderConfig: Record<number, { label: string; variant: 'default' | 'secondary' | 'destructive' }> = {
  0: { label: '未知', variant: 'secondary' },
  1: { label: '男', variant: 'default' },
  2: { label: '女', variant: 'destructive' },
}

function getVipLevelLabel(vipLevel: number): string {
  return VIP_LEVEL_OPTIONS.find(option => option.value === vipLevel)?.label ?? '-'
}

// 获取联系方式值
function getContactValue(contacts: Customer['contacts'], key: string): string {
  return contacts.find(c => c.key === key)?.value || '-'
}

export function CustomersTable({
  data,
  isLoading,
  onEdit,
  onViewDetail,
  onDelete,
  onTransfer,
  onManualOrder,
  onRecordService,
  preparingCustomerId,
  isPreparingManualOrder,
}: CustomersTableProps) {
  if (isLoading) {
    return (
      <div className="p-4 space-y-3">
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="flex items-center gap-4">
            <Skeleton className="h-4 w-20" />
            <Skeleton className="h-4 w-32" />
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-4 flex-1" />
          </div>
        ))}
      </div>
    )
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="w-[80px]">ID</TableHead>
          <TableHead>姓名</TableHead>
          <TableHead>手机号</TableHead>
          <TableHead>微信号</TableHead>
          <TableHead>性别</TableHead>
          <TableHead>年龄</TableHead>
          <TableHead>客户类型</TableHead>
          <TableHead>VIP等级</TableHead>
          <TableHead>状态</TableHead>
          <TableHead>锁定人</TableHead>
          <TableHead>跟踪人</TableHead>
          <TableHead>锁定时间</TableHead>
          <TableHead className="text-right">操作</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {!data?.length ? (
          <TableRow>
            <TableCell colSpan={13} className="h-24 text-center">
              暂无数据
            </TableCell>
          </TableRow>
        ) : (
          data.map(customer => {
            const genderInfo = genderConfig[customer.gender] || genderConfig[0]
            return (
              <TableRow key={customer.id}>
                <TableCell className="font-mono">{customer.id}</TableCell>
                <TableCell className="font-medium">{customer.name}</TableCell>
                <TableCell>{getContactValue(customer.contacts, 'mobile_1')}</TableCell>
                <TableCell>{getContactValue(customer.contacts, 'wechat_1')}</TableCell>
                <TableCell>
                  <Badge variant={genderInfo.variant}>{genderInfo.label}</Badge>
                </TableCell>
                <TableCell>{customer.age || '-'}</TableCell>
                <TableCell>{customer.category?.name || '-'}</TableCell>
                <TableCell>{getVipLevelLabel(customer.vip_level)}</TableCell>
                <TableCell>
                  {customer.blacklist ? (
                    <Badge variant="destructive">黑名单</Badge>
                  ) : (
                    <Badge variant="secondary">正常</Badge>
                  )}
                </TableCell>
                <TableCell>{customer.user_name || '-'}</TableCell>
                <TableCell>{customer.tracker_name || '-'}</TableCell>
                <TableCell className="text-muted-foreground text-sm">
                  {customer.locked_at || '-'}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      aria-label="详细信息"
                      onClick={() => onViewDetail(customer)}
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                    <PermissionGuard permission="service_record:create">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        title="录入服务"
                        onClick={() => onRecordService(customer)}
                      >
                        <Headset className="h-4 w-4" />
                      </Button>
                    </PermissionGuard>
                    <PermissionGuard permission="customer:edit">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => onEdit(customer)}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                    </PermissionGuard>
                    <PermissionGuard permission="customer:transfer">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => onTransfer(customer)}
                      >
                        <ArrowRightLeft className="h-4 w-4" />
                      </Button>
                    </PermissionGuard>
                    <PermissionGuard permission="order:create">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        aria-label={preparingCustomerId === customer.id ? '正在准备手动录单' : '手动录单'}
                        disabled={isPreparingManualOrder}
                        onClick={() => onManualOrder(customer)}
                      >
                        <ShoppingCart className="h-4 w-4" />
                      </Button>
                    </PermissionGuard>
                    <PermissionGuard permission="customer:delete">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-destructive hover:text-destructive"
                        onClick={() => onDelete(customer)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </PermissionGuard>
                  </div>
                </TableCell>
              </TableRow>
            )
          })
        )}
      </TableBody>
    </Table>
  )
}
